// Public Support Page - accessible from landing page without authentication
"use client";

import type React from "react";
import { useState } from "react";
import { ChevronLeft, Send, MessageCircle } from "lucide-react";
import { useRouter } from "next/navigation";
import { useToast } from "@/shared/hooks/use-toast";
import { motion } from "framer-motion";
import Link from "next/link";
import LandingFooter from "@/features/landing/components/LandingFooter";

import { captureError } from "@/shared/lib/observability";
/**
 * Public Support Page - accessible without login for users who need help.
 * Uses email for identification instead of user_id.
 */
const PublicSupportPage = () => {
    const router = useRouter();
    const { toast } = useToast();

    const [formData, setFormData] = useState({
        email: "",
        subject: "",
        feature: "",
        body: "",
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const featureOptions = [
        "Login/Signup Issues",
        "Forgot Password",
        "Account Access",
        "Daily Drip",
        "Look Lab",
        "Occasion Station",
        "Wardrobe based shopping assistance",
        "Image Based Shopping Assistance",
        "Credit Issue",
        "Payment issue",
        "Others",
    ];

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.email || !formData.subject || !formData.feature || !formData.body) {
            toast({
                title: "Missing Information",
                description: "Please fill in all required fields",
                variant: "destructive",
            });
            return;
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            toast({
                title: "Invalid Email",
                description: "Please enter a valid email address",
                variant: "destructive",
            });
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await fetch(
                `${process.env.NEXT_PUBLIC_API_URL}/api/v1/feedbackSupport/public`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        email: formData.email,
                        subject: formData.subject,
                        body: formData.body,
                        feature: formData.feature.toLowerCase(),
                    }),
                }
            );

            const data = await response.json();

            if (response.status === 429) {
                toast({
                    title: "Too Many Requests",
                    description: "You've submitted too many requests. Please try again in an hour.",
                    variant: "destructive",
                });
                return;
            }

            if (!response.ok) {
                throw new Error(data.message || "Failed to submit support request");
            }

            toast({
                title: "Thank you for your feedback!",
                description: "We'll get back to you at " + formData.email + " soon.",
            });
            setFormData({ email: "", subject: "", feature: "", body: "" });

            setTimeout(() => {
                router.push("/");
            }, 2000);
        } catch (error) {
            captureError(error, { feature: "support", op: "Support request error" });
            toast({
                title: "Submission Failed",
                description:
                    "Something went wrong while sending your feedback. Please check your connection and try again.",
                variant: "destructive",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#fffdfa]">
            {/* Navbar */}
            <nav
                className="relative flex items-center justify-center border-b-2 px-6 py-4"
                style={{ borderColor: "#ffd372" }}
            >
                <Link
                    href="/"
                    aria-label="Back to home"
                    className="absolute left-6 top-1/2 flex -translate-y-1/2 items-center gap-2 rounded-xl border-2 border-[#4c4c4c] px-3 py-1 font-winky text-sm text-[#4c4c4c] shadow-[2px_2px_0_#4c4c4c] transition-transform duration-200 hover:scale-105 hover:bg-[#4c4c4c] hover:text-[#fffdfa] active:translate-y-[2px] active:scale-95"
                >
                    <ChevronLeft />
                </Link>

                <motion.div
                    className="font-winky text-3xl font-black text-charcoal"
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: "spring", stiffness: 300 }}
                >
                    <Link href="/">
                        <img src="/logo.png" alt="Hey Hoku" width={256} height={263} className="h-8 w-auto cursor-pointer" />
                    </Link>
                </motion.div>
            </nav>

            {/* Main Content */}
            <div className="mx-auto w-full max-w-[430px] px-6 py-8">
                {/* Header */}
                <div className="text-center mb-8">
                    <div
                        className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
                        style={{ backgroundColor: "#ffd372" }}
                    >
                        <MessageCircle size={32} style={{ color: "#4c4c4c" }} />
                    </div>
                    <h1 className="font-winky text-3xl font-bold text-[#4c4c4c]">
                        Need Help? We're Here!
                    </h1>
                    <p
                        className="mt-2 text-base font-patrick leading-relaxed"
                        style={{ color: "#4c4c4c" }}
                    >
                        Can't log in or having other issues? Let us know and we'll help you out!
                    </p>
                </div>

                {/* Support Form */}
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div
                        className="rounded-3xl p-6 shadow-lg"
                        style={{
                            backgroundColor: "white",
                            boxShadow: "0 8px 0 #f0ead8, 0 12px 24px rgba(0,0,0,0.1)",
                        }}
                    >
                        {/* Email Field */}
                        <div className="mb-6">
                            <label htmlFor="support-email" className="mb-2 block font-winky text-base font-semibold text-[#4c4c4c]">
                                Your Email *
                            </label>
                            <input
                                id="support-email"
                                type="email"
                                value={formData.email}
                                onChange={(e) =>
                                    setFormData((prev) => ({ ...prev, email: e.target.value }))
                                }
                                placeholder="your.email@example.com"
                                className="w-full rounded-xl border-2 px-4 py-3 font-patrick text-base transition-transform duration-200 focus:outline-none focus:scale-[1.02]"
                                style={{
                                    borderColor: "#f0ead8",
                                    backgroundColor: "#fffdfa",
                                }}
                                required
                            />
                            <p className="mt-1 text-sm text-[#4c4c4c]/60 font-patrick">
                                We'll respond to this email address
                            </p>
                        </div>

                        {/* Subject Field */}
                        <div className="mb-6">
                            <label htmlFor="support-subject" className="mb-2 block font-winky text-base font-semibold text-[#4c4c4c]">
                                Subject *
                            </label>
                            <input
                                id="support-subject"
                                type="text"
                                value={formData.subject}
                                onChange={(e) =>
                                    setFormData((prev) => ({ ...prev, subject: e.target.value }))
                                }
                                placeholder="Brief description of your issue"
                                className="w-full rounded-xl border-2 px-4 py-3 font-patrick text-base transition-transform duration-200 focus:outline-none focus:scale-[1.02]"
                                style={{
                                    borderColor: "#f0ead8",
                                    backgroundColor: "#fffdfa",
                                }}
                                required
                            />
                        </div>

                        {/* Feature Dropdown */}
                        <div className="mb-6">
                            <label htmlFor="support-feature" className="mb-2 block font-winky text-base font-semibold text-[#4c4c4c]">
                                What do you need help with? *
                            </label>
                            <select
                                id="support-feature"
                                value={formData.feature}
                                onChange={(e) =>
                                    setFormData((prev) => ({ ...prev, feature: e.target.value }))
                                }
                                className="w-full rounded-xl border-2 px-4 py-3 font-patrick text-base transition-transform duration-200 focus:outline-none focus:scale-[1.02]"
                                style={{
                                    borderColor: "#f0ead8",
                                    backgroundColor: "#fffdfa",
                                }}
                                required
                            >
                                <option value="">Select an option</option>
                                {featureOptions.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Body Field */}
                        <div className="mb-6">
                            <label htmlFor="support-body" className="mb-2 block font-winky text-base font-semibold text-[#4c4c4c]">
                                Describe Your Issue *
                            </label>
                            <textarea
                                id="support-body"
                                value={formData.body}
                                onChange={(e) =>
                                    setFormData((prev) => ({ ...prev, body: e.target.value }))
                                }
                                placeholder="Please describe the issue you're experiencing in detail. Include any error messages or steps that led to the problem..."
                                rows={5}
                                className="w-full resize-none rounded-xl border-2 px-4 py-3 font-patrick text-base transition-transform duration-200 focus:outline-none focus:scale-[1.02]"
                                style={{
                                    borderColor: "#f0ead8",
                                    backgroundColor: "#fffdfa",
                                }}
                                required
                            />
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="flex w-full items-center justify-center gap-2 rounded-xl border border-[#4c4c4c] bg-[#ffd372] py-4 font-winky text-base font-bold transition-transform duration-200 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                                    Submitting...
                                </>
                            ) : (
                                <>
                                    <Send size={20} />
                                    Submit Support Request
                                </>
                            )}
                        </button>
                    </div>
                </form>

                {/* Footer Note */}
                <div className="text-center mt-8">
                    <p className="font-patrick text-base" style={{ color: "#4c4c4c" }}>
                        We typically respond within 24 hours. Thanks for your patience! 💛
                    </p>
                    <p className="font-patrick text-sm mt-4" style={{ color: "#4c4c4c" }}>
                        Already have an account?{" "}
                        <Link href="/login" className="text-[#89bff8] underline hover:text-[#ffd372]">
                            Log in here
                        </Link>
                    </p>
                    <p className="font-patrick text-sm mt-2" style={{ color: "#4c4c4c" }}>
                        See how we handle your data in our{" "}
                        <Link href="/legal/privacy" className="text-[#89bff8] underline hover:text-[#ffd372]">
                            Privacy Policy
                        </Link>.
                    </p>
                </div>
            </div>

            <LandingFooter />
        </div>
    );
};

export default PublicSupportPage;
